import React, {useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {BusinessHours as BusinessHoursModel} from '../../../models/api/businessHoursModel';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {InboxModel} from '../../../models/api/inboxModel';
import {
  getBusinessHoursClearConfirmLabel,
  getBusinessHoursClearDialogBody,
  getBusinessHoursClearDialogTitle,
  getBusinessHoursSaveConfirmLabel,
  getBusinessHoursSaveDialogBody,
  getBusinessHoursSaveDialogTitle,
} from '../../helpers/businessHoursHelpers';
import {BusinessHoursDialog} from './businessHoursDialog';
import {BusinessHoursDialogConfirmation} from './businessHoursDialogConfirmation';

/*
  Props.
*/

export interface BusinessHoursProps {
  businessHours: BusinessHoursModel;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  timeFormat: TimeFormatsEnum;
  teamName: string;
  onConfirm: HandlerOf<BusinessHoursModel>;
  onDisable: Handler;
  teamInboxesWithBusinessHours: ReadonlyArray<InboxModel>;
  hasUpdateError?: boolean;
}

/*
  Styles.
*/

const Wrap = styled.div`
  display: flex;
`;

/*
  Component.
*/

export const BusinessHours = (props: BusinessHoursProps) => {
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const businessHours = useRef<BusinessHoursModel | null>(null);

  const getTeamName = () => {
    if (props.teamName.toLowerCase().endsWith('team')) {
      return props.teamName;
    }

    return `${props.teamName} Team`;
  };

  const renderButton = () => (
    <Button
      variant={ButtonVariantsEnum.LINK}
      size={VisualSizesEnum.SMALL}
      interactionId="edit-business-hours"
    >
      <Trans id="edit-business-hours">Edit team business hours</Trans>
    </Button>
  );

  const renderModal = (modalProps: PopoverTransferProps) => {
    const handleHoursSave = (newBusinessHours: BusinessHoursModel) => {
      businessHours.current = newBusinessHours;
      setConfirmEdit(true);
    };

    const handleConfirmationOK = () => {
      if (businessHours.current) {
        props.onConfirm(businessHours.current);
        modalProps.onRequestClose();
        setConfirmEdit(false);
        setConfirmDisable(false);
      }
    };

    const handleDisableOk = () => {
      props.onDisable();
      modalProps.onRequestClose();
      setConfirmEdit(false);
      setConfirmDisable(false);
    };

    return (
      <>
        <BusinessHoursDialog
          {...props}
          onSave={handleHoursSave}
          onDisable={() => setConfirmDisable(true)}
          modalProps={modalProps}
        />
        {confirmEdit ? (
          <BusinessHoursDialogConfirmation
            onConfirm={handleConfirmationOK}
            onCancel={() => setConfirmEdit(false)}
            title={getBusinessHoursSaveDialogTitle(getTeamName())}
            body={getBusinessHoursSaveDialogBody()}
            confirmLabel={getBusinessHoursSaveConfirmLabel()}
          />
        ) : null}
        {confirmDisable ? (
          <BusinessHoursDialogConfirmation
            onConfirm={handleDisableOk}
            onCancel={() => setConfirmDisable(false)}
            title={getBusinessHoursClearDialogTitle(getTeamName())}
            body={getBusinessHoursClearDialogBody()}
            confirmLabel={getBusinessHoursClearConfirmLabel()}
          />
        ) : null}
      </>
    );
  };

  return (
    <Wrap>
      <PopoverCoordinator
        renderButton={renderButton}
        renderModal={renderModal}
        isModalInitiallyOpen={props.hasUpdateError}
      />
    </Wrap>
  );
};
